/**
 * Synchronization helpers for images generated through Phoenix's
 * `ImageRenderingSystem` during finalization.
 *
 * Phoenix renders screenshots with a separate, offscreen camera and renderer.
 * The scene it renders, however, is the live React Three Fiber scene. This used
 * to create two independent timing problems:
 *
 * 1. Dimension labels use Troika Text, which generates glyph geometry
 *    asynchronously. A screenshot could therefore be taken before the current
 *    label text had finished syncing, producing arrows and guide lines without
 *    their values.
 * 2. The labels are wrapped in Drei Billboards. Their orientation is updated
 *    from React Three Fiber's live camera in a `useFrame` callback, not from
 *    Phoenix's offscreen capture camera. When the two cameras pointed in
 *    different directions, the offscreen renderer could see labels edge-on or
 *    sideways. Fixed delays only made both races less likely and could not
 *    guarantee a correct image.
 *
 * Before each capture, these helpers wait for the current Troika glyph data,
 * align the live camera with the capture camera, and allow real render frames
 * to run so camera-dependent objects update. Finalization fails on timeout
 * instead of silently uploading a broken image. The caller restores the live
 * camera after capture.
 *
 * Longer-term alternatives are to fix Phoenix so its capture pipeline updates
 * billboards and other frame-driven objects using the capture camera, expose a
 * first-class "scene ready"/text-sync hook from the SDK, or capture from the
 * live R3F canvas after temporarily moving its camera. Removing billboards
 * from dimension labels would also avoid the camera mismatch, but would change
 * their interactive behavior. Those approaches require Phoenix changes and,
 * for Metalworks, an SDK upgrade; this module is the local compatibility fix.
 */
import type CameraControls from 'camera-controls'
import type { Object3D, Scene } from 'three'
import { Vector3 } from 'three'

const RENDER_SYNC_TIMEOUT = 5000
const CAPTURE_CAMERA_UPDATE_DELTA = 1000000

interface TroikaTextObject extends Object3D {
  text: string
  textRenderInfo: {
    parameters: {
      text: string
    }
  } | null
  sync: () => void
}

const isTroikaTextObject = (object: Object3D): object is TroikaTextObject => {
  const candidate = object as Partial<TroikaTextObject>

  return typeof candidate.text === 'string' && typeof candidate.sync === 'function' && 'textRenderInfo' in candidate
}

const isTextSynchronized = (text: TroikaTextObject) => text.textRenderInfo?.parameters.text === text.text

const waitForTextSync = (text: TroikaTextObject, timeout: number) => {
  if (isTextSynchronized(text)) {
    return Promise.resolve()
  }

  return new Promise<void>((resolve, reject) => {
    const handleSyncComplete = () => {
      // A second sync can be queued while the first one is still running. Only
      // resolve when the render info belongs to the current label value.
      if (!isTextSynchronized(text)) {
        return
      }

      clearTimeout(timeoutId)
      text.removeEventListener('synccomplete', handleSyncComplete)
      resolve()
    }

    const timeoutId = setTimeout(() => {
      text.removeEventListener('synccomplete', handleSyncComplete)
      reject(new Error(`Timed out waiting for dimension label to render: "${text.text}"`))
    }, timeout)

    text.addEventListener('synccomplete', handleSyncComplete)
    text.sync()
  })
}

/**
 * Wait for React Three Fiber to execute camera-dependent frame callbacks such
 * as Drei's Billboard. A timeout fails finalization instead of producing a
 * silently broken customer PDF when the render loop is not advancing.
 */
export const waitForRenderFrames = async (frameCount = 2, timeout = RENDER_SYNC_TIMEOUT) => {
  for (let frame = 0; frame < frameCount; frame += 1) {
    await new Promise<void>((resolve, reject) => {
      const frameId = requestAnimationFrame(() => {
        clearTimeout(timeoutId)
        resolve()
      })

      const timeoutId = setTimeout(() => {
        cancelAnimationFrame(frameId)
        reject(new Error('Timed out waiting for the 3D scene to render. Keep this tab visible and try again.'))
      }, timeout)
    })
  }
}

/** Wait until all currently mounted Troika text meshes contain current glyph data. */
export const waitForSceneText = async (scene: Scene, timeout = RENDER_SYNC_TIMEOUT) => {
  const pendingText: Array<Promise<void>> = []

  scene.traverse(object => {
    if (isTroikaTextObject(object) && object.text.length > 0) {
      pendingText.push(waitForTextSync(object, timeout))
    }
  })

  await Promise.all(pendingText)
}

/**
 * Point the live R3F camera in exactly the same direction as the independent
 * camera Phoenix will use for its offscreen render. Dimension-label billboards
 * otherwise keep facing the live camera and can be captured edge-on.
 */
export const synchronizeLiveCamera = async (captureControls: CameraControls, liveControls: CameraControls) => {
  const capturePosition = captureControls.getPosition(new Vector3(), false)
  const captureTarget = captureControls.getTarget(new Vector3(), false)

  await liveControls.setLookAt(
    capturePosition.x,
    capturePosition.y,
    capturePosition.z,
    captureTarget.x,
    captureTarget.y,
    captureTarget.z,
    false
  )
  liveControls.update(CAPTURE_CAMERA_UPDATE_DELTA)
}

/** Ensure labels have glyphs, then render frames that orient them to the synchronized camera. */
export const synchronizeSceneForCapture = async (scene: Scene) => {
  await waitForSceneText(scene)
  await waitForRenderFrames()
}
